import { createRoute } from "@tanstack/react-router";
import { appAuthRoute } from "./index";
import { ENTITY } from "../../constants";
import { checkPermissionsAndRedirect } from "../../helpers/checkPermissions";
import {
  callToActionFormLoader,
  callToActionListLoader,
  callToActionViewLoader,
} from "../loaders/callToAction";
import { CallToActionBreadcrumb } from "../../breadcrumbs/callToAction";
import Spinner from "../../components/general/Spinner";
// import loading pages
import EntityListLoading from "../../pages/entities/loading/EntityListLoading";
import EntityViewLoading from "../../pages/entities/loading/EntityViewLoading";
import EntityEditLoading from "../../pages/entities/loading/EntityEditLoading";

const { CALLTOACTION } = ENTITY;

export const callToActionRouteFactory = (parent = appAuthRoute) => {
  // If parent route is appAuthRoute => the route is NOT nested (ie. its directly under appAuthRoute)
  // If parent route is NOT appAuthRoute => route is nested
  const isNestedRoute = parent !== appAuthRoute;

  // Root

  const callToActionRoute = createRoute({
    getParentRoute: () => parent,
    path: CALLTOACTION.type.plural,
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: CALLTOACTION.type.singular, action: "READ" },
      ]),
    staticData: {
      breadcrumb: CALLTOACTION.label.upFirstPlural,
    },
  });

  // List callToActions

  const callToActionListRoute = createRoute({
    getParentRoute: () => callToActionRoute,
    path: "/",
    loader: callToActionListLoader,
    pendingComponent: () => <EntityListLoading entity={CALLTOACTION} />,
  }).lazy(() => import("./callToAction.lazy").then((d) => d.CallToActionListRoute));

  // Create callToAction

  const callToActionCreateRoute = createRoute({
    getParentRoute: () => callToActionRoute,
    path: "create",
    beforeLoad: () =>
      checkPermissionsAndRedirect([
        { feature: CALLTOACTION.type.singular, action: "CREATE" },
      ]),
    staticData: {
      breadcrumb: "Create new",
    },
    loader: callToActionFormLoader,
    pendingComponent: () => <EntityEditLoading entity={CALLTOACTION} />,
  }).lazy(() => import("./callToAction.lazy").then((d) => d.CallToActionCreateRoute));

  // View callToAction

  const callToActionViewRootRoute = createRoute({
    getParentRoute: () => callToActionRoute,
    path: "$callToActionId",
    staticData: {
      breadcrumb: CallToActionBreadcrumb,
    },
    loader: callToActionViewLoader,
  });

  // Edit callToAction

  const callToActionEditRoute = createRoute({
    getParentRoute: () => callToActionViewRootRoute,
    path: "edit",
    staticData: {
      breadcrumb: "Edit",
    },
    loader: callToActionFormLoader,
    pendingComponent: () => <EntityEditLoading entity={CALLTOACTION} />,
  }).lazy(() => import("./callToAction.lazy").then((d) => d.CallToActionEditRoute));

  // View callToAction Layout

  const callToActionViewLayoutRoute = createRoute({
    getParentRoute: () => callToActionViewRootRoute,
    id: "callToAction-view-layout",
    pendingComponent: () => <EntityViewLoading entity={CALLTOACTION} />,
  }).lazy(() =>
    import("./callToAction.lazy").then((d) => d.CallToActionViewLayoutRoute)
  );

  // View callToAction - General Tab

  const callToActionViewLayoutGeneralTabRoute = createRoute({
    getParentRoute: () => callToActionViewLayoutRoute,
    path: "/",
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./callToAction.lazy").then((d) => d.CallToActionViewLayoutGeneralTabRoute)
  );

  // View callToAction - About Tab

  const callToActionViewLayoutAboutTabRoute = createRoute({
    getParentRoute: () => callToActionViewLayoutRoute,
    path: "about",
    staticData: {
      breadcrumb: "About",
    },
    pendingComponent: Spinner,
  }).lazy(() =>
    import("./callToAction.lazy").then((d) => d.CallToActionViewLayoutAboutTabRoute)
  );


  // Route tree
  callToActionRoute.addChildren([
    // if the route is nested within a parent route, we do not provide ListRoute
    // because the route is already nested in a TabListRoute
    ...(isNestedRoute ? [] : [callToActionListRoute]),
    callToActionCreateRoute,
    callToActionViewRootRoute.addChildren([
      callToActionEditRoute,
      callToActionViewLayoutRoute.addChildren([
        callToActionViewLayoutGeneralTabRoute,
        callToActionViewLayoutAboutTabRoute,
      ]),
    ]),
  ]);

  return callToActionRoute;
};

const callToActionRoute = callToActionRouteFactory();

export default callToActionRoute;
